import { useEffect, useRef, useState } from "react";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../axiosInstance";

export function fileSize(bytes) {
  return bytes < 1048576 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function SupportChatAttachment({ file, paused }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const active = useRef(true);
  const objectUrls = useRef(new Set());
  const request = useRef(null);
  const isImage = ["image/png", "image/jpeg", "image/webp"].includes(file.content_type);
  useEffect(() => {
    active.current = true;
    const urls = objectUrls.current;
    return () => { active.current = false; request.current?.abort(); urls.forEach(url => URL.revokeObjectURL(url)); urls.clear(); };
  }, []);

  const closePreview = () => {
    if (preview) { URL.revokeObjectURL(preview); objectUrls.current.delete(preview); }
    setPreview(null);
  };
  const open = async (showPreview = false) => {
    if (busy || paused) return;
    setBusy(true); setError("");
    request.current = new AbortController();
    try {
      // Fetch with the actual signed-in principal. Never render a server/SAS URL.
      const response = await axiosInstance.get(`/support-chat/attachments/${file.id}`, { responseType: "blob", signal: request.current.signal });
      if (!active.current) return;
      const blob = new Blob([response.data], { type: file.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      objectUrls.current.add(url);
      if (showPreview && isImage) setPreview(url);
      else {
        const link = document.createElement("a"); link.href = url;
        link.download = file.filename.replace(/[\\/]/g, "_");
        document.body.appendChild(link); link.click(); link.remove();
        // Let the browser begin reading before releasing the private object URL.
        setTimeout(() => { URL.revokeObjectURL(url); objectUrls.current.delete(url); }, 1000);
      }
    } catch (cause) {
      if (active.current && cause?.code !== "ERR_CANCELED") setError([404, 410].includes(cause?.response?.status) ? "This file is no longer available." : "File could not be loaded. Try again when connected.");
    } finally { if (active.current) setBusy(false); }
  };
  return <div className="support-file-wrap">
    <div className="support-file support-file-saved">
      <span className="support-file-icon">{isImage ? <ImageOutlinedIcon fontSize="small" /> : <AttachFileIcon fontSize="small" />}</span>
      <div className="support-file-copy"><strong title={file.filename}>{file.filename}</strong><span>{fileSize(file.size_bytes)}</span></div>
      {busy ? <CircularProgress size={20} aria-label="Loading attachment" /> : <>
        {isImage && <Tooltip title="Preview image"><span><IconButton aria-label={`Preview ${file.filename}`} disabled={paused} onClick={() => open(true)}><ImageOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>}
        <Tooltip title="Download file"><span><IconButton aria-label={`Download ${file.filename}`} disabled={paused} onClick={() => open(false)}><DownloadIcon fontSize="small" /></IconButton></span></Tooltip>
      </>}
    </div>
    {error && <p className="support-file-error" role="alert">{error}</p>}
    <Dialog open={Boolean(preview)} onClose={closePreview} maxWidth="md" fullWidth>
      <DialogTitle className="support-preview-title">{file.filename}<IconButton aria-label="Close image preview" onClick={closePreview}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent>{preview && <img src={preview} alt={file.filename} className="support-image-preview" />}</DialogContent>
    </Dialog>
  </div>;
}
